import { AdSettings } from './ads';

interface ClassicProxy {
  host: string;
  port: number;
  secure?: boolean;
}

export interface Site {
  production?: string;
  classic?: string;
  analytics?: string;
  brands?: string;
  forum?: string;
  mobile?: string;
}

interface Api {
  default: string;
}

type CampaignDateMap = {
  [key: string]: string;
};

export interface Campaigns {
  base?: string;
  previousCampaignDates?: CampaignDateMap;
}

interface Legacy {
  brand?: string;
  category?: string;
  fight?: string;
  forum?: string;
  info?: string;
  lists?: string;
  minhembio?: string;
  plugin?: string;
  product?: string;
  raw?: string;
  // eslint-disable-next-line camelcase
  store_info?: string;
  // eslint-disable-next-line camelcase
  user_login?: string;
  // eslint-disable-next-line camelcase
  mobile_site?: string;
}

export interface Url {
  campaigns?: string;
  legacy?: Legacy;
}

type SocialNetwork = {
  link?: string;
};

type Facebook = SocialNetwork & {
  enableLogin?: boolean;
  clientID?: string;
  clientSecret?: string;
  callbackURL?: string;
};

type Twitter = SocialNetwork & {
  enableLogin?: boolean;
  callbackURL?: string;
  consumerKey: string;
  consumerSecret: string;
};

type SocialNetworks = {
  facebook?: Facebook;
  instagram?: SocialNetwork;
  twitter?: Twitter;
};

interface Email {
  noreply?: string;
}

export interface Cdn {
  base?: string;
  origin?: string;
  images?: string;
  offerImages?: string;
}

interface Path {
  path?: string;
  relativePath?: string;
}

interface Routes {
  themes?: Path;
  campaign?: Path;
  productAlert?: Path;
  productCompare?: Path;
  giftGuide?: Path;
  shopOfTheYear?: Path;
  shopOfTheYearCategory?: Path;
  shopOfTheYearWinners?: Path;
  editorialContentListing?: Path;
  categoryIndex?: Path;
}

interface PopularCampaignFilterPath {
  title: string;
  path: string;
  description?: string;
}

interface InfoPageMap {
  userAgreement?: number;
  priceInformation?: number;
  privacyPolicy?: number;
  cookiePolicy?: number;
  contact?: number;
  about?: number;
  registerShop?: number;
  media?: number;
  disclaimer?: number;
  aboutReviews?: number;
  adlink?: string;
  themes?: string;
  careers?: string;
  articles?: string;
  shopOfTheYear?: string;
  categoryIndex?: string;
}

interface Newsletter {
  apiUrl?: string;
  apiKey?: string;
  list?: string;
  gansubFormId?: string;
}

export interface Apps {
  ios?: string;
  android?: string;
}

interface Responsive {
  cookieVersion?: string;
  cookieUrl?: string;
  privacyUrl?: string;
  privacySettingsUrl?: string;
}

interface PrivacySettings {
  responsive?: Responsive;
  desktop?: Responsive;
  mobile?: Responsive;
  native?: Responsive;
}

export interface Ads extends AdSettings {
  active: boolean;
}
interface ModuleSettings {
  enabled: boolean;
  datePlaceholderFormat?: string;
}

interface Modules {
  priceInfo?: ModuleSettings;
  categoryInfo?: ModuleSettings;
  userReviewPurchaseDate?: ModuleSettings;
  readableProductInfo?: ModuleSettings;
}

export enum PrivacySettingToggles {
  BehaviourTrackingImprovement = 'behavior_tracking_improvement',
  BehaviorTrackingMarketing = 'behavior_tracking_marketing',
  BehaviorTrackingSurveys = 'behavior_tracking_surveys',
  PersonalizedAds = 'personalized_ads',
  Personalization = 'personalization',
}

export type PrivacySettingToggle = { [key in PrivacySettingToggles]: boolean };

interface trackingToggle {
  name?:
    | PrivacySettingToggles.BehaviourTrackingImprovement
    | PrivacySettingToggles.BehaviorTrackingMarketing
    | PrivacySettingToggles.BehaviorTrackingSurveys
    | PrivacySettingToggles.PersonalizedAds
    | PrivacySettingToggles.Personalization;
  default: boolean;
}

interface ShopOfTheYear {
  isVotingOpen?: boolean;
  year?: number;
  featuredArticles?: object;
}

interface Tracking {
  googleTagManager: string;
  facebookPixel: string;
  pulse?: string;
}

export interface Amp {
  enabled?: boolean;
  ads?: boolean;
  tracking: Tracking;
}

interface PriceList {
  showInternational?: boolean;
  featuredAlternativePriceType?: string;
  hiddenListOptions?: Array<string>;
}

interface CdnCache {
  enabled: boolean;
  mode: 'internal' | 'external';
}

export interface Market {
  countryCode: MarketCodes;
  active: boolean;
  sitename?: string;
  permanentRedirect?: boolean;
  redirectHosts?: string[];
  superSearchEndpoint?: string;
  trackerProxy?: string;
  useClassicUrls?: boolean;
  classicProxy?: ClassicProxy;
  hrefLang?: string[];
  locale: string;
  sites: Site;
  api?: Api;
  campaigns: Campaigns;
  googleTagManager?: string;
  translation: string;
  timezone?: string;
  currencyCode?: string;
  url: Url;
  socialNetworks: SocialNetworks;
  googleSiteVerification?: string;
  campaignFeaturedList?: Array<{ campaignId: number; listId: number }>;
  email?: Email;
  cdn: Cdn;
  routes?: Routes;
  popularCampaignFilterPaths?: PopularCampaignFilterPath[];
  infoPages?: number[];
  infoPageMap?: InfoPageMap;
  featuredArticle?: number;
  newsletter?: Newsletter;
  apps?: Apps;
  ads: Ads;
  privacySettings?: PrivacySettings;
  modules?: Modules;
  showSchibstedCoBranding?: boolean;
  showClassicSwitch: boolean;
  trackingToggles?: trackingToggle[];
  shopOfTheYear?: ShopOfTheYear;
  amp?: Amp;
  showProductInformationTexts?: boolean;
  priceList?: PriceList;
  structuredData?: any;
  cdnCache?: CdnCache;
  useHashForProductTabs?: boolean;
  lazyLoadIntercom?: boolean;
  showPromoText?: boolean;
  liftSimilarProductsOnProductPage?: boolean;
}

export type MarketCodes =
  | 'dk'
  | 'fi'
  | 'fr'
  | 'gb'
  | 'ie'
  | 'it'
  | 'no'
  | 'nz'
  | 'pl'
  | 'se';

// export enum MarketCodes {
//   dk = 'dk',
//   es = 'es',
//   fi = 'fi',
//   fr = 'fr',
//   gb = 'gb',
//   ie = 'ie',
//   it = 'it',
//   no = 'no',
//   nz = 'nz',
//   pl = 'pl',
//   se = 'se',
// }

export type Markets = {
  [key in MarketCodes]: Market;
};
